import React from "react";
import Webcam from "react-webcam";
import * as faceapi from "face-api.js";

interface DetectionResult {
  box: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export const loadModelsAndDetectFaces = (
  webcamRef: React.RefObject<Webcam>,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  ellipseRef: React.RefObject<SVGEllipseElement>,
  callback: (isFaceInsideArea: boolean, isFaceApiReady: boolean) => void
): number | null => {
  const startFaceDetection = async () => {
    const { video } = webcamRef.current || {};
    const ellipse = ellipseRef.current;
    const canvas = canvasRef.current;

    if (!video || !ellipse || !canvas) return;

    try {
      const detections = await faceapi.detectAllFaces(
        video,
        new faceapi.SsdMobilenetv1Options({ maxResults: 1 })
      );

      const displaySize = { width: video.width, height: video.height - 50 };
      faceapi.matchDimensions(canvas, displaySize);

      const context = canvas.getContext("2d");
      if (!context) return;

      context.clearRect(0, 0, canvas.width, canvas.height);
      const resizedDetections = faceapi.resizeResults(detections, displaySize);

      if (detections.length === 0) {
        callback(false, true);
        return;
      }

      const { x, y, width, height } =
        (resizedDetections[0] as DetectionResult)?.box || {};
      if (!x || !y || !width || !height) return;

      // Get absolute positions
      const canvasRect = canvas.getBoundingClientRect();
      const ellipseRect = ellipse.getBoundingClientRect();

      // Normalize coordinates to canvas dimensions
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      // Convert to canvas-relative coordinates (0-1 range)
      const faceCenterX = (x + width / 2) / canvasWidth;
      const faceCenterY = (y + height / 2) / canvasHeight;
      const faceWidth = width / canvasWidth;
      const faceHeight = height / canvasHeight;

      // Convert ellipse to canvas-relative coordinates
      const ellipseCenterX =
        (ellipseRect.left - canvasRect.left + ellipseRect.width / 2) /
        canvasWidth;
      const ellipseCenterY =
        (ellipseRect.top - canvasRect.top + ellipseRect.height / 2) /
        canvasHeight;
      const ellipseRadiusX = ellipseRect.width / 2 / canvasWidth;
      const ellipseRadiusY = ellipseRect.height / 2 / canvasHeight;

      // Check if face center is within ellipse using ellipse equation
      const dx = faceCenterX - ellipseCenterX;
      const dy = faceCenterY - ellipseCenterY;

      // Calculate distance from center
      const centerDistance = Math.sqrt(
        Math.pow(faceCenterX - 0.5, 2) + Math.pow(faceCenterY - 0.5, 2)
      );

      // Check if face is within circular target area
      const isFaceInsideArea =
        centerDistance < 0.25 && // Within 25% from center
        width > 0.2; // Minimum face size

      const normalizedDistance =
        Math.pow(dx / ellipseRadiusX, 2) + Math.pow(dy / ellipseRadiusY, 2);

      // Additional checks for face size
      // const isFaceInsideArea =
      //   normalizedDistance <= 1 && faceWidth > 0.2 && faceHeight > 0.2;

      // console.log({
      //   isFaceInsideArea,
      //   normalizedDistance,
      //   face: {
      //     centerX: faceCenterX,
      //     centerY: faceCenterY,
      //     width: faceWidth,
      //     height: faceHeight,
      //   },
      //   ellipse: {
      //     centerX: ellipseCenterX,
      //     centerY: ellipseCenterY,
      //     radiusX: ellipseRadiusX,
      //     radiusY: ellipseRadiusY,
      //   },
      // });

      callback(isFaceInsideArea, true);
    } catch (error) {
      console.error("Face detection error:", error);
      callback(false, true);
    }
  };

  const setupFaceDetection = async () => {
    try {
      await faceapi.nets.ssdMobilenetv1.loadFromUri("/models");
      return setInterval(startFaceDetection, 500);
    } catch (error) {
      callback(false, false);
      return null;
    }
  };

  return setupFaceDetection() as unknown as number;
};
